import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, Download, FileOutput, FileUp, Sparkles } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { inlineInput } from '@/lib/form-styles'
import { checkLv, druckbildAnfrage, fetchLv, ladeLvDatei, setLvUnit, type LvCheck, type LvCheckCode, type LvDruckOptionen } from '../api/calculation-lv.api'
import { fetchUnits } from '../api/calculation-positions.api'
import type { CalcLv, CalcUnit, Calculation } from '../types/calculation.types'
import { ExportButton } from '@/components/ui/ExportButton'
import { useTranslation } from 'react-i18next'
import { PRUEFUNG } from '../lib/lv-pruefung'
import { LvAusgaben } from './LvAusgaben'
import { NuBereich } from './anfragen/NuBereich'
import { LvAusgebenDialog } from './LvAusgebenDialog'
import { LvEinlesenDialog } from './LvEinlesenDialog'

const PHASE: Record<string, string> = { DRAFT: 'Entwurf', TENDER: 'Ausschreibung', OFFER: 'Angebot', ORDER: 'Auftrag', OTHER: '—' }

const EBENE: Record<string, string> = { LV: 'LV-Summe', HG: 'Hauptgruppen', OG: 'Obergruppen', LG: 'Leistungsgruppen', ULG: 'Unterleistungsgruppen' }


/**
 * AP-37 9. — **Reiter LV**: Kopf (Format, Phase, Datei, Lohn/Sonstiges
 * gefordert), Zähler ausgepreist / offen / Lücken, Einheiten ohne Übersetzung
 * (übersetzen an allen Positionen, wahlweise gemerkt), Hinweise des Lesers,
 * **Geänderte Ausschreibung einlesen** (neue Fassung), **Prüfen** (4.12,
 * Phase I). Ausgeben kommt mit Phase K.
 */
export function LvTab({
  kalkulation,
  onGeaendert,
  onNeueFassung,
  kiLuecken,
  kiPruefung,
}: {
  kalkulation: Calculation
  onGeaendert: () => void
  onNeueFassung: (id: string) => void
  /** Detailblatt N 15. (N6): Lücken → „Vorschlagen" — nur mit `.AI`, Recht und offener Fassung */
  kiLuecken?: () => void
  /** Detailblatt N 7. (N7): die zweite Gruppe der Prüfliste, „Hinweise der KI" */
  kiPruefung?: ReactNode
}) {
  const { user } = useAuthStore()
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const darfEinlesen = erfuelltRecht('calculation.lv.import', user?.permissions) && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [lv, setLv] = useState<CalcLv | null>(null)
  const [einheiten, setEinheiten] = useState<CalcUnit[]>([])
  const [merken, setMerken] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [neu, setNeu] = useState(false)
  const [pruefung, setPruefung] = useState<LvCheck | null>(null)
  const [ausgeben, setAusgeben] = useState(false)
  const { t } = useTranslation()
  // Detailblatt K 5: Druckbild — für sich ein Download bzw. eine E-Mail, nicht gespeichert (K-19).
  const [druck, setDruck] = useState<LvDruckOptionen>({ longText: false, preliminary: false, blank: false, summaryOnly: false })
  // Detailblatt K: ausgeben mit `calculation.lv.export`; heute ONLV (GAEB mit K-GAEB, B 2063 mit dem Erzeuger).
  const darfAusgeben = erfuelltRecht('calculation.lv.export', user?.permissions) && !kalkulation.isTemplate

  const laden = useCallback(async () => {
    try {
      setLv(await fetchLv(kalkulation.id))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das LV ließ sich nicht laden.'))
    }
  }, [kalkulation.id])
  useEffect(() => {
    void laden()
    fetchUnits().then(setEinheiten).catch(() => setEinheiten([]))
  }, [laden, kalkulation.updatedAt])

  if (!lv) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>

  const uebersetzen = async (raw: string, unitId: string) => {
    setFehler(null)
    try {
      const r = await setLvUnit(kalkulation.id, { raw, unitId: unitId || null, remember: merken })
      setHinweis(`„${raw}" an ${r.updated} Position(en) übersetzt${merken ? ' und für künftige LVs gemerkt' : ''}.`)
      await laden()
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Einheit ließ sich nicht übersetzen.'))
    }
  }

  const pruefen = async () => {
    setFehler(null)
    try {
      setPruefung(await checkLv(kalkulation.id))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Prüfung hat nicht geklappt.'))
    }
  }

  const c = lv.counts
  const kachel = (titel: string, wert: string | number, warnung = false) => (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className={`text-lg font-semibold tabular-nums ${warnung ? 'text-amber-700' : ''}`}>{wert}</div>
    </div>
  )

  return (
    <div className="space-y-4">
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-muted-foreground">{hinweis}</p>}
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-0.5 text-sm">
            <div className="text-base font-semibold">{lv.title}</div>
            <div className="text-muted-foreground">
              {lv.format} {lv.formatVersion} · {PHASE[lv.phase] ?? lv.phase}
              {lv.lvDate && ` · vom ${new Date(lv.lvDate).toLocaleDateString('de-DE')}`} · eingelesen {new Date(lv.importedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
            </div>
            {lv.requiresPriceSplit && <div className="font-medium">Lohn und Sonstiges sind gefordert — der EP wird nach 4.7 geteilt.</div>}
            {lv.allowedDiscounts && (
              <div className="text-muted-foreground">
                Nachlass zugelassen {lv.allowedDiscounts.base === 'PRICE_SHARES' ? 'je Preisanteil' : 'auf Summen'}
                {lv.allowedDiscounts.levels.length > 0 && `: ${lv.allowedDiscounts.levels.map((l) => EBENE[l] ?? l).join(', ')}`}
              </div>
            )}
            {lv.format === 'ONLV' && !lv.allowedDiscounts && lv.namespace && <div className="text-muted-foreground">Das LV lässt keinen Nachlass zu.</div>}
            {lv.priceProcedure && lv.priceProcedure !== 'Preisangebotsverfahren' && <div className="font-medium text-amber-700 dark:text-amber-400">{lv.priceProcedure} — dieses Preisverfahren wird nicht unterstützt.</div>}
            {lv.reimportForGaps && (
              <div className="text-amber-700 dark:text-amber-400">Die Bieterlücken stehen hier nur als Sammelfrage. „Geänderte Ausschreibung einlesen“ mit derselben Datei zeigt sie einzeln.</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-sm text-primary-foreground" onClick={() => void pruefen()}>
              <ClipboardCheck className="h-4 w-4" /> Prüfen
            </button>
            {darfAusgeben && (lv.format === 'ONLV' || lv.format === 'GAEB' || lv.format === 'B2063') && (
              <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-sm text-primary-foreground" onClick={() => setAusgeben(true)}>
                <FileOutput className="h-4 w-4" /> Ausgeben …
              </button>
            )}
            {darfAusgeben && (lv.format === 'ONLV' || lv.format === 'GAEB' || lv.format === 'B2063') && (
              <ExportButton
                label="Druckbild"
                className="!h-8"
                defaultFilename={`angebots-lv-${kalkulation.number}`}
                request={(format, orientation) => druckbildAnfrage(kalkulation.id, format, orientation, druck)}
                mail={{ source: 'EXPORT_CALCULATION_LV_PRINT', title: t('mailExport.titles.EXPORT_CALCULATION_LV_PRINT'), subject: `${t('mailExport.titles.EXPORT_CALCULATION_LV_PRINT')} ${kalkulation.label} ${lv.title}` }}
                extra={
                  <div className="space-y-1">
                    {(
                      [
                        ['longText', 'mit Langtext'],
                        ['preliminary', 'mit Vorbemerkungen'],
                        ['blank', 'ohne Preise (Blanko)'],
                        ['summaryOnly', 'nur Zusammenstellung'],
                      ] as const
                    ).map(([k, text]) => (
                      <label key={k} className="flex items-center gap-2">
                        <input type="checkbox" checked={!!druck[k]} onChange={(e) => setDruck((d) => ({ ...d, [k]: e.target.checked }))} /> {text}
                      </label>
                    ))}
                  </div>
                }
              />
            )}
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => void ladeLvDatei(kalkulation.id, lv.fileName)}>
              <Download className="h-4 w-4" /> {lv.fileName}
            </button>
            {darfEinlesen && kalkulation.canRevise && (
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => setNeu(true)}>
                <FileUp className="h-4 w-4" /> Geänderte Ausschreibung einlesen …
              </button>
            )}
          </div>
        </div>
      </section>

      <div className={`grid gap-3 ${c.notOffered ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        {kachel('Positionen', c.items - c.text)}
        {kachel('ausgepreist', c.priced)}
        {kachel('offen', c.open, c.open > 0)}
        {kachel('Lücken offen', c.gapsOpen, c.gapsOpen > 0)}
        {c.notOffered > 0 && kachel('nicht angeboten', c.notOffered)}
      </div>
      {kiLuecken && c.gapsOpen > 0 && (
        <div className="-mt-1 flex justify-end">
          <button type="button" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" onClick={kiLuecken}>
            <Sparkles className="h-4 w-4" /> Lücken vorschlagen …
          </button>
        </div>
      )}

      {pruefung && (
        <section className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">
              Prüfliste {pruefung.ok ? '— nichts, was die Abgabe aufhält' : `— ${pruefung.items.filter((f) => !PRUEFUNG[f.code].hinweis).length} Befund(e)`}
            </h3>
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setPruefung(null)}>
              schließen
            </button>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Die Prüfliste warnt, sie sperrt nichts. EP von Hand zählt ab {pruefung.threshold.toLocaleString('de-DE')} % Abweichung (Einstellungen).
            {pruefung.discountPercent !== null && ` Nachlass gesetzt: ${pruefung.discountPercent.toLocaleString('de-DE')} %.`}
          </p>
          {(Object.keys(PRUEFUNG) as LvCheckCode[])
            .filter((code) => pruefung.counts[code] > 0)
            .map((code) => (
              <div key={code} className="mb-3">
                <div className={`text-sm font-medium ${PRUEFUNG[code].hinweis ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}>
                  {PRUEFUNG[code].titel} · {pruefung.counts[code]}
                </div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {pruefung.items
                    .filter((f) => f.code === code)
                    .slice(0, 50)
                    .map((f) => (
                      <li key={`${code}-${f.positionId}`} className="flex gap-2">
                        <Link to={`?tab=positionen&pos=${f.positionId}`} className="w-24 shrink-0 tabular-nums text-primary hover:underline">
                          {f.label}
                        </Link>
                        <span className="min-w-0 flex-1 truncate">{f.shortText}</span>
                        {f.detail && <span className="shrink-0 text-xs text-muted-foreground">{f.detail}</span>}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </section>
      )}
      {/* Detailblatt N 7. (N7): zweite Gruppe der Prüfliste — nur auf Knopfdruck, sie kostet. */}
      {pruefung && kiPruefung}

      <LvAusgaben kalkulationId={kalkulation.id} nummer={kalkulation.number} stand={`${kalkulation.updatedAt}|${kalkulation.submittedAt ?? ''}`} />

      {/* Detailblatt K 4 — Anfragen an Nachunternehmer auf den Anfragen aus O. */}
      {!kalkulation.isTemplate && <NuBereich kalkulation={kalkulation} lvFormat={lv?.format} onGeaendert={onGeaendert} />}

      {ausgeben && (
        <LvAusgebenDialog
          kalkulation={kalkulation}
          onClose={() => setAusgeben(false)}
          onDone={() => {
            setAusgeben(false)
            onGeaendert()
          }}
        />
      )}

      {lv.unitsUntranslated.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-1 text-sm font-semibold">Einheiten ohne Übersetzung</h3>
          <p className="mb-2 text-xs text-muted-foreground">Die Einheit laut LV bleibt, wie sie ist — übersetzt wird für Blatt, Ausdruck und Projekt.</p>
          <table className="w-full max-w-xl text-sm">
            <tbody>
              {lv.unitsUntranslated.map((u) => (
                <tr key={u.unit} className="border-t border-border/60">
                  <td className="py-1 pr-2 font-medium">{u.unit}</td>
                  <td className="py-1 pr-2 text-xs text-muted-foreground">{u.count} Position(en)</td>
                  <td className="py-1">
                    <select className={`${inlineInput} !w-40`} disabled={!darf} value="" onChange={(e) => void uebersetzen(u.unit, e.target.value)} aria-label={`Übersetzung für ${u.unit}`}>
                      <option value="">übersetzen als …</option>
                      {einheiten.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.code}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {darf && (
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} /> für künftige LVs merken (gilt auch im Projekt)
            </label>
          )}
        </section>
      )}

      {lv.notes && lv.notes.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-1 text-sm font-semibold">Hinweise beim Einlesen</h3>
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
            {lv.notes.map((n, i) => (
              <li key={i}>{n.text}</li>
            ))}
          </ul>
        </section>
      )}

      {neu && (
        <LvEinlesenDialog
          kalkulation={kalkulation}
          neueFassung
          onClose={() => setNeu(false)}
          onDone={(id) => {
            setNeu(false)
            if (id) onNeueFassung(id)
          }}
        />
      )}
    </div>
  )
}
